"use client";

import { AnimatePresence, motion } from "framer-motion";
import { MessageSquare, Mic, Music, Palette } from "lucide-react";
import { useEffect, useState } from "react";

/**
 * 실시간 피드백 랜딩 히어로용 "살아있는" 강의실 미리보기.
 * 강사·학생 아바타가 대화(말풍선)를 주고받고, 학생이 무대로 이동하며,
 * 화이트보드 문구가 순환한다. 순수 데코 애니메이션(상태 기반 루프).
 */

interface Turn {
  speaker: "teacher" | "me";
  text: string;
  board: string;
  /** 학생이 무대(중앙)로 이동하는지 */
  onStage: boolean;
}

const TURNS: Turn[] = [
  {
    speaker: "teacher",
    text: "한 소절 불러볼까요? 🎤",
    board: "오늘의 피드백\n호흡 · 리듬 · 표현",
    onStage: false,
  },
  {
    speaker: "me",
    text: "네! 해볼게요 ✨",
    board: "🔴 라이브 코칭 중",
    onStage: true,
  },
  {
    speaker: "teacher",
    text: "후렴에서 브레스 추가!",
    board: "후렴 감정 표현 ↑",
    onStage: true,
  },
  {
    speaker: "me",
    text: "이렇게요? 🎶",
    board: "🔴 라이브 코칭 중",
    onStage: true,
  },
  {
    speaker: "teacher",
    text: "완벽해요! 훨씬 좋아졌어요 👏",
    board: "좋아요! 한 번 더 🔁",
    onStage: false,
  },
];

function Avatar({
  headClass,
  bodyClass,
  label,
  talking,
}: {
  headClass: string;
  bodyClass: string;
  label: string;
  talking: boolean;
}) {
  return (
    <div className="relative flex flex-col items-center">
      {/* 말하는 중 표시 링 */}
      <AnimatePresence>
        {talking && (
          <motion.span
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: [0.5, 0, 0.5], scale: [0.9, 1.5, 0.9] }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="absolute -top-1 h-12 w-12 rounded-full border-2 border-emerald-400/70 sm:h-16 sm:w-16"
          />
        )}
      </AnimatePresence>
      {/* 살짝 위아래로 흔들리는 idle 모션 */}
      <motion.div
        animate={{ y: [0, -3, 0] }}
        transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
        className="flex flex-col items-center"
      >
        <div
          className={`h-12 w-12 rounded-full sm:h-16 sm:w-16 ${headClass}`}
        />
        <div
          className={`h-16 w-14 rounded-t-[24px] sm:h-20 sm:w-20 ${bodyClass}`}
        />
      </motion.div>
      <span className="mt-1 rounded bg-black/50 px-2 py-0.5 text-[10px] text-white">
        {label}
      </span>
    </div>
  );
}

function SpeechBubble({ text }: { text: string }) {
  return (
    <motion.div
      key={text}
      initial={{ opacity: 0, y: 8, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.9 }}
      transition={{ duration: 0.3 }}
      className="absolute -top-9 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-2xl bg-white px-3 py-1.5 text-[11px] font-semibold text-gray-800 shadow-lg sm:text-xs"
    >
      {text}
      <span className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-white" />
    </motion.div>
  );
}

export default function AnimatedClassroomPreview() {
  const [turnIndex, setTurnIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setTurnIndex(prev => (prev + 1) % TURNS.length);
    }, 2600);
    return () => clearInterval(id);
  }, []);

  const turn = TURNS[turnIndex];

  return (
    <div className="rounded-[28px] border border-white/10 bg-[#15151d] p-3 shadow-2xl shadow-violet-950/40">
      <div className="relative aspect-[4/3] overflow-hidden rounded-[20px] border border-white/5 bg-gradient-to-b from-[#353145] via-[#252431] to-[#17171f]">
        {/* 무대 스포트라이트 (학생이 무대에 오르면 밝아짐) */}
        <motion.div
          animate={{ opacity: turn.onStage ? 0.5 : 0 }}
          transition={{ duration: 0.6 }}
          className="absolute bottom-0 right-[10%] h-[70%] w-[45%] rounded-full bg-gradient-to-t from-violet-400/40 to-transparent blur-2xl"
        />

        {/* 화이트보드 */}
        <div className="absolute left-1/2 top-[12%] h-[30%] w-[52%] -translate-x-1/2 overflow-hidden rounded-lg border-4 border-slate-600 bg-slate-100">
          <AnimatePresence mode="wait">
            <motion.div
              key={turn.board}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="flex h-full items-center justify-center whitespace-pre-line px-2 text-center text-xs font-semibold leading-snug text-slate-600 sm:text-sm"
            >
              {turn.board}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* 바닥 */}
        <div className="absolute bottom-0 left-[8%] h-[18%] w-[84%] rounded-t-xl bg-[#5a3d2d]" />

        {/* 떠오르는 음표 */}
        {[0, 1, 2].map(i => (
          <motion.span
            key={i}
            className="absolute bottom-[28%] text-violet-200/70"
            style={{ left: `${58 + i * 8}%` }}
            animate={{ y: [0, -46], opacity: [0, 1, 0] }}
            transition={{
              duration: 2.8,
              repeat: Infinity,
              delay: i * 0.7,
              ease: "easeOut",
            }}
          >
            <Music className="h-3.5 w-3.5" />
          </motion.span>
        ))}

        {/* 강사 아바타 (고정) */}
        <div className="absolute bottom-[14%] left-[20%] flex flex-col items-center">
          <AnimatePresence>
            {turn.speaker === "teacher" && <SpeechBubble text={turn.text} />}
          </AnimatePresence>
          <Avatar
            headClass="bg-violet-300"
            bodyClass="bg-violet-600"
            label="강사"
            talking={turn.speaker === "teacher"}
          />
        </div>

        {/* 학생(나) 아바타 - 무대 호출 시 중앙으로 이동 */}
        <motion.div
          className="absolute bottom-[10%] right-[19%] flex flex-col items-center"
          animate={{ x: turn.onStage ? -70 : 0 }}
          transition={{ type: "spring", stiffness: 120, damping: 16 }}
        >
          <AnimatePresence>
            {turn.speaker === "me" && <SpeechBubble text={turn.text} />}
          </AnimatePresence>
          <Avatar
            headClass="bg-amber-200"
            bodyClass="bg-indigo-500"
            label="나"
            talking={turn.speaker === "me"}
          />
        </motion.div>

        {/* Live 배지 */}
        <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 text-xs text-white backdrop-blur">
          <motion.span
            animate={{ opacity: [1, 0.3, 1] }}
            transition={{ duration: 1.4, repeat: Infinity }}
            className="h-2 w-2 rounded-full bg-emerald-400"
          />
          Live · 2명
        </div>

        {/* 하단 컨트롤 도크 */}
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2 rounded-full border border-white/10 bg-black/50 p-2 backdrop-blur">
          {[Mic, MessageSquare, Palette].map((Icon, index) => {
            const micActive = index === 0;
            return (
              <motion.span
                key={index}
                animate={
                  micActive
                    ? { scale: [1, 1.12, 1], backgroundColor: "rgba(52,211,153,0.25)" }
                    : {}
                }
                transition={{ duration: 1.2, repeat: Infinity }}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-white"
              >
                <Icon className="h-4 w-4" />
              </motion.span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
